import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, ImagePlus, X } from "lucide-react";
import ReviewStars from "./ReviewStars";
import { submitReviewApi } from "../api";

const MAX_PHOTOS = 3;
const MAX_BYTES = 8 * 1024 * 1024;
const ALLOWED = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];

/**
 * Public review submission form. Writes to /api/reviews with status=pending
 * for admin moderation. Up to 3 photos (8 MB each).
 */
const ReviewForm = ({ productId, onDone }) => {
  const [rating, setRating] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", title: "", text: "" });
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const addFiles = (incoming) => {
    const next = [];
    for (const f of incoming) {
      if (files.length + next.length >= MAX_PHOTOS) break;
      if (!ALLOWED.includes(f.type)) {
        toast.error(`${f.name}: unsupported type`);
        continue;
      }
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name}: exceeds 8 MB`);
        continue;
      }
      next.push(f);
    }
    if (!next.length) return;
    setFiles((prev) => [...prev, ...next]);
    setPreviews((prev) => [...prev, ...next.map((f) => ({ url: URL.createObjectURL(f), name: f.name }))]);
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    setPreviews((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      URL.revokeObjectURL(prev[idx]?.url);
      return next;
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (rating < 1) { toast.error("Please pick a star rating."); return; }
    if (!form.name.trim() || !form.email.trim() || !form.text.trim()) {
      toast.error("Please fill name, email and your review.");
      return;
    }
    if (form.text.trim().length < 10) {
      toast.error("Please write at least 10 characters.");
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("product_id", productId);
      fd.append("rating", String(rating));
      fd.append("name", form.name.trim());
      fd.append("email", form.email.trim());
      fd.append("title", form.title.trim());
      fd.append("text", form.text.trim());
      for (const f of files) fd.append("photos", f);
      const res = await submitReviewApi(fd);
      toast.success(res.message || "Thanks — your review is in.");
      setRating(0);
      setForm({ name: "", email: "", title: "", text: "" });
      setFiles([]);
      setPreviews([]);
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={onSubmit} data-testid="review-form" className="bg-[var(--ivory)] border border-[var(--sand)] p-6 lg:p-8 space-y-5">
      <div>
        <p className="text-xs tracking-[0.2em] uppercase text-[var(--copper)] mb-3">Your rating</p>
        <ReviewStars value={rating} size={28} interactive onChange={setRating} label="Pick a star rating" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <input
          type="text"
          required
          value={form.name}
          onChange={upd("name")}
          placeholder="Your name"
          data-testid="review-name"
          className="h-11 px-4 text-sm border border-[var(--sand)] focus:outline-none focus:border-[var(--copper)] bg-white"
        />
        <input
          type="email"
          required
          value={form.email}
          onChange={upd("email")}
          placeholder="your@email.com (not published)"
          data-testid="review-email"
          className="h-11 px-4 text-sm border border-[var(--sand)] focus:outline-none focus:border-[var(--copper)] bg-white"
        />
      </div>

      <input
        type="text"
        value={form.title}
        onChange={upd("title")}
        placeholder="Headline (optional)"
        data-testid="review-title"
        className="w-full h-11 px-4 text-sm border border-[var(--sand)] focus:outline-none focus:border-[var(--copper)] bg-white"
      />

      <textarea
        required
        value={form.text}
        onChange={upd("text")}
        placeholder="How does it look, feel and light up?"
        rows={5}
        maxLength={2000}
        data-testid="review-text"
        className="w-full px-4 py-3 text-sm border border-[var(--sand)] focus:outline-none focus:border-[var(--copper)] bg-white resize-none"
      />

      {/* Photos */}
      <div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => { addFiles(Array.from(e.target.files || [])); e.target.value = ""; }}
        />
        <div className="flex items-center flex-wrap gap-3">
          {previews.map((p, idx) => (
            <div key={p.url} className="relative w-20 h-20 bg-[var(--sand)] overflow-hidden group">
              <img src={p.url} alt={p.name} className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removeFile(idx)}
                aria-label="Remove photo"
                className="absolute top-1 right-1 bg-[var(--espresso)]/80 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          {files.length < MAX_PHOTOS && (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid="review-add-photo"
              className="w-20 h-20 border border-dashed border-[var(--sand)] hover:border-[var(--copper)] flex items-center justify-center text-[var(--copper)]"
              aria-label="Add photo"
            >
              <ImagePlus className="w-5 h-5" />
            </button>
          )}
        </div>
        <p className="text-xs text-[var(--espresso)]/50 mt-2">Up to {MAX_PHOTOS} photos, 8 MB each. JPG/PNG/WEBP/HEIC.</p>
      </div>

      <button
        type="submit"
        disabled={sending}
        data-testid="review-submit"
        className="h-12 px-10 vc-btn-copper text-xs tracking-[0.25em] uppercase inline-flex items-center justify-center min-w-[180px]"
      >
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Post review"}
      </button>
      <p className="text-[11px] text-[var(--espresso)]/50">Reviews are published after a quick check by the studio.</p>
    </form>
  );
};

export default ReviewForm;
